import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import Typeahead from 'design-system/src/components/Typeahead/Typeahead';
import { RadioBlockGroup, RadioOptionWrapper, RadioLabel } from 'design-system/src/components/Radio/Radio';
import { Input } from '../Input';
import { FormLabel } from '../FormLabel';
import { DropdownWithPlaceholder, DropdownItem } from '../Dropdown';
import { type BenefitPreferenceType, type SiteOfCare } from '../../api';
import { type PracticeBenefitPreference } from './BenefitPreferences';
import {
  useCreateBenefitPreference,
  useUpdateBenefitPreference,
  type ApiError,
} from '../../hooks/useBenefitPreferences';
import { formatAddress } from '../../utils';

const NDC_LENGTH = 11;
const NDC_PATTERN = /^\d{11}$/;
const NPI_LENGTH = 10;
const NPI_PATTERN = /^\d{10}$/;

export type BenefitPreferenceFormData = {
  drug_name: string;
  drug_ndc: string;
  rendering_provider_npi: string;
  hcpcs: string;
  cpt: string;
  site_of_care_id: string;
  benefits_preference: BenefitPreferenceType | '';
};

type BenefitPreferenceFormModalProps = {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Available sites of care for the dropdown */
  sites: SiteOfCare[];
  /** Available drugs for the drug search */
  drugs: string[];
  /** Existing practice-level preference to edit. When set, the modal operates in edit mode. */
  preference?: PracticeBenefitPreference;
  /** Initial values for the form (used when duplicating) */
  initialData?: Partial<BenefitPreferenceFormData>;
};

// Styled component kept for MUI-specific overrides that can't be done with Tailwind
const DrugTypeahead = styled(Typeahead)`
  .MuiAutocomplete-listbox {
    max-width: 100%;

    .MuiAutocomplete-option {
      white-space: normal;
      word-wrap: break-word;
    }
  }
`;

/**
 * Shared modal for adding or editing a benefit preference.
 * Pass `preference` to edit; omit it (or pass `initialData`) to add/duplicate.
 */
const getDuplicateErrorMessage = (error: ApiError): string | null => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'object' && detail?.code === 'DUPLICATE_PREFERENCE') {
    return detail.message || 'A preference already exists for this drug and formulation combination.';
  }
  return null;
};

const BenefitPreferenceFormModal: React.FC<BenefitPreferenceFormModalProps> = ({
  open,
  onClose,
  sites,
  drugs,
  preference,
  initialData,
}) => {
  const isEditing = !!preference;
  const [formError, setFormError] = useState<string | null>(null);

  const defaults: BenefitPreferenceFormData = {
    drug_name: preference?.drug_name ?? initialData?.drug_name ?? '',
    drug_ndc: preference?.drug_ndc ?? initialData?.drug_ndc ?? '',
    rendering_provider_npi: preference?.rendering_provider_npi ?? initialData?.rendering_provider_npi ?? '',
    hcpcs: preference?.hcpcs ?? initialData?.hcpcs ?? '',
    cpt: preference?.cpt ?? initialData?.cpt ?? '',
    site_of_care_id: preference?.site_of_care_id ?? initialData?.site_of_care_id ?? '',
    benefits_preference: preference?.benefits_preference ?? initialData?.benefits_preference ?? '',
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<BenefitPreferenceFormData>({ defaultValues: defaults });

  const handleClose = () => {
    reset();
    setFormError(null);
    onClose();
  };

  // Clear form error when any field changes
  const watchedFields = watch();
  useEffect(() => {
    if (formError) {
      setFormError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFields.drug_name, watchedFields.drug_ndc]);

  const createPreference = useCreateBenefitPreference({
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Benefit preference added</Toast>);
      handleClose();
    },
    onError: (error) => {
      const duplicateMessage = getDuplicateErrorMessage(error);
      if (duplicateMessage) {
        setFormError(duplicateMessage);
      } else {
        toast.custom(() => <StandardErrorToast />);
      }
    },
  });

  const updatePreference = useUpdateBenefitPreference({
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Benefit preference updated</Toast>);
      handleClose();
    },
    onError: (error) => {
      const duplicateMessage = getDuplicateErrorMessage(error);
      if (duplicateMessage) {
        setFormError(duplicateMessage);
      } else {
        toast.custom(() => <StandardErrorToast />);
      }
    },
  });

  const isPending = createPreference.isPending || updatePreference.isPending;

  const onSubmit = (data: BenefitPreferenceFormData) => {
    const payload = {
      drug_name: data.drug_name,
      drug_form: null,
      drug_ndc: data.drug_ndc || null,
      hcpcs: data.hcpcs,
      cpt: data.cpt || null,
      site_of_care_id: data.site_of_care_id || null,
      benefits_preference: data.benefits_preference as BenefitPreferenceType,
      provider_npi: null,
      rendering_provider_npi: data.rendering_provider_npi || null,
    };

    if (isEditing && preference) {
      updatePreference.mutate({ preferenceId: preference.id, data: payload });
    } else {
      createPreference.mutate(payload);
    }
  };

  const isFormValid =
    watch('drug_name') && watch('drug_ndc') && watch('hcpcs') && watch('cpt') && watch('benefits_preference');

  const drugOptions = useMemo(() => drugs.map((drug) => ({ value: drug, description: drug })), [drugs]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      hasUnsavedChanges={isDirty}
      footerButtonConfig={{
        showCancelButton: true,
        submitButton: {
          label: 'Save',
          disabled: !isFormValid,
          isLoading: isPending,
          onClick: handleSubmit(onSubmit),
        },
      }}
    >
      <ModalTitle>{isEditing ? 'Edit' : 'Add'} fulfillment preference</ModalTitle>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <FormLabel htmlFor="drug-name">Drug</FormLabel>
            <Controller
              control={control}
              name="drug_name"
              rules={{ required: 'Drug is required' }}
              render={({ field }) => (
                <DrugTypeahead
                  labelId="drug-name"
                  options={drugOptions}
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors.drug_name}
                  placeholder="Select drug"
                />
              )}
            />
            {errors.drug_name && <ErrorMessage>{errors.drug_name.message}</ErrorMessage>}
          </div>

          <div className="flex gap-4">
            <div className="flex w-1/2 flex-col">
              <Controller
                control={control}
                name="drug_ndc"
                rules={{
                  required: 'NDC is required',
                  pattern: {
                    value: NDC_PATTERN,
                    message: 'NDC must be exactly 11 digits',
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="NDC"
                    placeholder="e.g. 00000000000"
                    hasError={!!errors.drug_ndc}
                    {...field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, NDC_LENGTH);
                      field.onChange(value);
                    }}
                    maxLength={NDC_LENGTH}
                  />
                )}
              />
              {errors.drug_ndc && <ErrorMessage>{errors.drug_ndc.message}</ErrorMessage>}
            </div>

            <div className="flex w-1/2 flex-col">
              <Controller
                control={control}
                name="rendering_provider_npi"
                rules={{
                  pattern: {
                    value: NPI_PATTERN,
                    message: 'NPI must be exactly 10 digits',
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="Rendering NPI (optional)"
                    placeholder="e.g. 1234567890"
                    hasError={!!errors.rendering_provider_npi}
                    {...field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, NPI_LENGTH);
                      field.onChange(value);
                    }}
                    maxLength={NPI_LENGTH}
                  />
                )}
              />
              {errors.rendering_provider_npi && <ErrorMessage>{errors.rendering_provider_npi.message}</ErrorMessage>}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex w-1/2 flex-col">
              <Controller
                control={control}
                name="hcpcs"
                rules={{
                  required: 'HCPCS code is required',
                  pattern: {
                    value: /^[A-Za-z]\d{4}$/,
                    message: 'HCPCS code must be 1 letter followed by 4 digits (e.g., J1745)',
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="HCPCS code"
                    placeholder="e.g. J1745"
                    hasError={!!errors.hcpcs}
                    {...field}
                    value={field.value.toUpperCase()}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.toUpperCase().slice(0, 5);
                      field.onChange(value);
                    }}
                    maxLength={5}
                  />
                )}
              />
              {errors.hcpcs && <ErrorMessage>{errors.hcpcs.message}</ErrorMessage>}
            </div>

            <div className="flex w-1/2 flex-col">
              <Controller
                control={control}
                name="cpt"
                rules={{
                  required: 'CPT code is required',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'CPT code must be 5 digits (e.g., 96413)',
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="Admin CPT code"
                    placeholder="e.g. 96413"
                    hasError={!!errors.cpt}
                    {...field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      field.onChange(value);
                    }}
                    maxLength={5}
                  />
                )}
              />
              {errors.cpt && <ErrorMessage>{errors.cpt.message}</ErrorMessage>}
            </div>
          </div>

          <div className="flex flex-col">
            <FormLabel htmlFor="site-of-care">Site of care (optional)</FormLabel>
            <Controller
              control={control}
              name="site_of_care_id"
              render={({ field }) => (
                <DropdownWithPlaceholder
                  id="site-of-care"
                  placeholder="Select site of care"
                  value={field.value}
                  onChange={field.onChange}
                  $hasError={!!errors.site_of_care_id}
                >
                  <DropdownItem value="">No site selected - will prompt on intake</DropdownItem>
                  {sites.map((site) => {
                    const fullAddress = formatAddress(
                      [site.address_line_1, site.address_line_2].filter(Boolean).join(', '),
                      site.city,
                      site.state,
                      site.zipcode,
                    );
                    return (
                      <DropdownItem key={site.id} value={site.id}>
                        {fullAddress}
                      </DropdownItem>
                    );
                  })}
                </DropdownWithPlaceholder>
              )}
            />
            {errors.site_of_care_id && <ErrorMessage>{errors.site_of_care_id.message}</ErrorMessage>}
          </div>

          <div className="flex flex-col gap-2">
            <FormLabel>Primary fulfillment preference</FormLabel>
            <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
              If denied, you will have the option to pursue the other benefit.
            </Typography>
            <Controller
              control={control}
              name="benefits_preference"
              rules={{ required: 'Benefit preference is required' }}
              render={({ field }) => (
                <RadioBlockGroup $variant="outlined">
                  {(
                    [
                      {
                        value: 'buy_and_bill',
                        label: 'Buy-and-bill',
                        description: 'Your practice purchases and bills the payer directly',
                      },
                      {
                        value: 'white_bagging',
                        label: 'Specialty pharmacy (white bagging)',
                        description: 'The specialty pharmacy will ship the medication to the site of care',
                      },
                      {
                        value: 'prompt_everytime',
                        label: 'Ask me each time',
                        description: 'Tandem will ask you to choose a fulfillment option for each patient',
                      },
                    ] as const
                  ).map((option) => (
                    <RadioOptionWrapper
                      id={`preference-${option.value}`}
                      name="benefits-preference"
                      value={option.value}
                      checked={field.value === option.value}
                      onChange={() => field.onChange(option.value)}
                      key={option.value}
                      variant="outlined"
                      hasError={!!errors.benefits_preference}
                    >
                      <div className="flex flex-col gap-0.5">
                        <RadioLabel>{option.label}</RadioLabel>
                        <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                          {option.description}
                        </Typography>
                      </div>
                    </RadioOptionWrapper>
                  ))}
                </RadioBlockGroup>
              )}
            />
            {errors.benefits_preference && <ErrorMessage>{errors.benefits_preference.message}</ErrorMessage>}
          </div>

          {formError && (
            <div className="mt-2 rounded-medium border border-red-200 bg-red-50 px-4 py-3">
              <Typography styledAs="bodySmallDMSans" color="var(--red-600)">
                {formError}
              </Typography>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BenefitPreferenceFormModal;
